import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, Box, Button, IconButton,
  Tooltip, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
  Skeleton, Chip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon,
  Link as LinkIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { portalAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface PortalData {
  portalToken: string;
  portalUrl: string;
}

const PortalLinkCard: React.FC = () => {
  const { user } = useAuth();
  const [portal, setPortal]           = useState<PortalData | null>(null);
  const [loading, setLoading]         = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar]       = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const isAdmin = user?.role === 'company_admin';

  useEffect(() => {
    loadPortalToken();
  }, []);

  const loadPortalToken = async () => {
    try {
      setLoading(true);
      const data = await portalAPI.getPortalToken();
      setPortal(data);
    } catch (err: any) {
      showSnackbar('Failed to load portal link', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!portal?.portalUrl) return;
    try {
      await navigator.clipboard.writeText(portal.portalUrl);
      showSnackbar('Portal link copied to clipboard!', 'success');
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = portal.portalUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      showSnackbar('Portal link copied!', 'success');
    }
  };

  const handleRegenerate = async () => {
    setConfirmOpen(false);
    setRegenerating(true);
    try {
      const data = await portalAPI.regeneratePortalToken();
      setPortal(data);
      showSnackbar('New portal link generated. The old link is now invalid.', 'success');
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to regenerate link', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LinkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Staff Request Portal
            </Typography>
            <Chip label="Public" size="small" color="success" variant="outlined" />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Share this link with staff to let them submit vehicle requests — no login required.
            Keep it private; anyone with the link can submit a request.
          </Typography>

          {/* Link display */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            }}
          >
            {loading ? (
              <Skeleton variant="text" sx={{ flex: 1, fontSize: 14 }} />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  wordBreak: 'break-all',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              >
                {portal?.portalUrl || '—'}
              </Typography>
            )}

            <Tooltip title="Copy link">
              <span>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  disabled={loading || !portal}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Open in new tab">
              <span>
                <IconButton
                  size="small"
                  onClick={() => window.open(portal?.portalUrl, '_blank')}
                  disabled={loading || !portal}
                >
                  <OpenIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<CopyIcon />}
              onClick={handleCopy}
              disabled={loading || !portal}
            >
              Copy Link
            </Button>

            {isAdmin && (
              <Tooltip title="Generates a new link. The current link will stop working immediately.">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    startIcon={<RefreshIcon />}
                    onClick={() => setConfirmOpen(true)}
                    disabled={loading || regenerating}
                  >
                    {regenerating ? 'Regenerating...' : 'Regenerate Link'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {!isAdmin && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Only Company Admins can regenerate the portal link.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Regenerate confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Regenerate Portal Link?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will generate a new portal link and <strong>immediately invalidate the current one</strong>.
            Any staff who have the current link bookmarked will no longer be able to access the portal
            until you share the new link with them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRegenerate} color="warning" variant="contained" autoFocus>
            Yes, Regenerate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PortalLinkCard;